/**
 * Quem é o dono deste aparelho — e a conferência que impede a segunda saída
 * da tela trancada de virar troca de conta.
 *
 * Por que isto existe, com todas as letras: "Não consigo desbloquear" pede
 * um e-mail e um código, e o código chega no e-mail **digitado**. Sem
 * conferir de quem é o aparelho, qualquer pessoa que tenha conta neste app —
 * e o telefone emprestado circula justamente entre elas — digitava o próprio
 * endereço, recebia o código no próprio aparelho e o portão abria sobre o
 * Dexie **do dono**: nomes e endereços ainda ali, porque offline o motor
 * para em `semRede` antes de qualquer purga.
 *
 * E o pior vinha depois: no primeiro ciclo online com outra conta, o motor
 * (`packages/sync/src/motor.ts`) esvazia a fila, zera os cursores e limpa o
 * que era da conta anterior. As visitas do dono ainda não sincronizadas
 * somem em silêncio, sem desfazer. A saída de emergência era, na prática, um
 * botão de trocar de conta apagando o banco.
 *
 * **O dono não é só a sessão viva.** Essa foi a primeira versão desta regra,
 * e ela deixava aberto o estado "trava ligada + sem sessão", que é
 * alcançável sem nada de estranho: a preferência da trava vive em
 * `estado['trava']` no Dexie e **não é apagada ao sair da conta**, enquanto
 * os dados locais continuam ali até o primeiro ciclo online com outra conta.
 * Nesse estado, quem estivesse com o telefone na mão digitava o próprio
 * e-mail, recebia o código, destrancava e via os dados do dono. Era o mesmo
 * defeito fechado antes, sobrando pela porta do logout.
 *
 * Por isso o endereço do último dono conhecido é **lembrado** ao lado da
 * preferência da trava, na mesma tabela `estado` do Dexie, e é ele que vale
 * quando não há sessão. A regra, então:
 *
 * - **Com sessão**, só o endereço dela abre — e ele é gravado como o dono
 *   conhecido, para valer depois que a sessão acabar.
 * - **Sem sessão, mas com dono lembrado**, só o endereço dele abre.
 * - **Sem sessão e sem dono lembrado**, endereço livre: é um aparelho
 *   virgem, não há dono a proteger, e o portão é a própria porta de entrada
 *   do app.
 * - **Sessão que não dá para ler** conta como sessão: falhar fecha. O mesmo
 *   vale para o banco que não responde na hora de ler o dono lembrado.
 *
 * A recusa é **genérica** de propósito em todos os casos: dizer "o e-mail
 * deste aparelho é fulano@..." entregaria a quem está com o telefone na mão
 * exatamente o dado que a trava esconde.
 *
 * A leitura é local (`auth.getSession()` lê o armazenamento do supabase-js,
 * que neste app é o próprio Dexie) e por isso vale offline — que é onde a
 * trava mais fecha.
 *
 * O ADR-0006 depende desta guarda: é ela que torna segura a **adoção** das
 * linhas escritas sem conta na troca de conta.
 */

import { ErroDaTrava } from './chaveDoAparelho.js';
import type { ArmazemDaPreferencia } from './trava.js';

/**
 * O tanto de cliente que a conferência exige. Declarado como forma, e não
 * importado como `Cliente`, para o teste passar um dublê de duas linhas.
 */
export interface ClienteComSessao {
  auth: {
    getSession(): Promise<{
      data: { session: { user: { email?: string | null } } | null };
    }>;
  };
}

/** O tanto de cliente que a lembrança exige: o aviso de mudança de sessão,
 *  com o e-mail dentro. */
export interface ClienteQueAnunciaSessao extends ClienteComSessao {
  auth: ClienteComSessao['auth'] & {
    onAuthStateChange(
      cb: (
        evento: string,
        sessao: { user: { email?: string | null } } | null,
      ) => void,
    ): { data: { subscription: { unsubscribe(): void } } };
  };
}

/** A chave na tabela `estado` do Dexie, vizinha da preferência da trava
 *  (`'trava'`). Vizinha de propósito: as duas guardam a mesma decisão vista
 *  de dois ângulos, e quem apagar uma tem de pensar na outra. */
export const CHAVE_DO_DONO = 'trava:dono';

/**
 * `null` = não há dono nenhum neste aparelho. Um objeto com `email: null` é
 * o caso desconfortável: **há** dono, mas não se sabe o endereço dele
 * (sessão ilegível, banco fora do ar, ou usuário sem e-mail) — e aí nada
 * abre por esta porta.
 */
export type ContaDoAparelho = { email: string | null } | null;

/** Grava o endereço do dono. Falhar não impede nada agora: a sessão viva
 *  continua sendo a fonte enquanto durar, e a próxima gravação tenta de
 *  novo. */
async function gravarDono(
  armazem: ArmazemDaPreferencia,
  email: string,
): Promise<void> {
  try {
    await armazem.put({ chave: CHAVE_DO_DONO, valor: email });
  } catch {
    // Não poder lembrar não impede valer nesta sessão.
  }
}

/**
 * Registra o dono deste aparelho e continua registrando a cada mudança de
 * sessão. Devolve a função que cancela a assinatura.
 *
 * Chamado uma vez, no arranque do app (`iniciarApp`): sem isto, o endereço
 * só seria conhecido se a tela trancada já tivesse consultado a sessão
 * alguma vez, e um aparelho em que a trava nunca fechou perderia o dono ao
 * sair da conta.
 *
 * **Nada de `await` dentro do callback de `onAuthStateChange`**, e nada de
 * `getSession()` lá dentro: o supabase-js segura o lock de auth durante o
 * callback (ver `app/sessao.ts`). O e-mail vem no próprio evento.
 *
 * Sair da conta **não apaga** o dono lembrado: é justamente o estado que
 * esta lembrança existe para cobrir.
 */
export function lembrarDonoDoAparelho(
  cliente: ClienteQueAnunciaSessao,
  armazem: ArmazemDaPreferencia,
): () => void {
  void cliente.auth
    .getSession()
    .then(({ data }) => {
      const email = data.session?.user.email;
      if (email) return gravarDono(armazem, email);
      return undefined;
    })
    .catch(() => undefined);
  const { data } = cliente.auth.onAuthStateChange((_evento, sessao) => {
    const email = sessao?.user.email;
    if (email) void gravarDono(armazem, email);
  });
  return () => {
    data.subscription.unsubscribe();
  };
}

/**
 * De quem é este aparelho: a sessão viva, se houver; senão o dono lembrado.
 *
 * A sessão viva também **atualiza** a lembrança — quem entrou por aqui é o
 * dono a partir de agora.
 */
export async function contaDoAparelho(
  cliente: ClienteComSessao,
  armazem: ArmazemDaPreferencia,
): Promise<ContaDoAparelho> {
  try {
    const { data } = await cliente.auth.getSession();
    if (data.session !== null) {
      const email = data.session.user.email ?? null;
      if (email !== null) await gravarDono(armazem, email);
      return { email };
    }
  } catch {
    // Um banco que não responde não é prova de que não há conta — e é
    // exatamente o estado em que um invasor teria interesse. Fecha.
    return { email: null };
  }
  try {
    const linha = await armazem.get(CHAVE_DO_DONO);
    const lembrado = linha?.valor;
    return lembrado === undefined || lembrado === ''
      ? null
      : { email: lembrado };
  } catch {
    // Mesmo raciocínio: não conseguir ler o dono não é prova de que não há
    // um. A chave do aparelho (Face ID) continua abrindo; o que fecha aqui é
    // só a saída por código.
    return { email: null };
  }
}

/** Comparação de endereço: sem espaços em volta e sem caixa. O GoTrue
 *  normaliza o e-mail para minúsculas, e o usuário digita como quiser. */
function mesmoEndereco(a: string, b: string): boolean {
  return a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();
}

/**
 * Este endereço pode entrar por aqui? Lança se não — e a mensagem não
 * revela qual é o endereço que poderia.
 */
export function conferirDono(digitado: string, conta: ContaDoAparelho): void {
  if (conta === null) return;
  if (conta.email !== null && mesmoEndereco(digitado, conta.email)) return;
  throw new ErroDaTrava(
    'Este não é o e-mail da conta deste aparelho. Entre com ele, ou peça o aparelho a quem o emprestou.',
  );
}
